import React, { useMemo, useState } from "react";
import {
  GridColDef,
  GridSortItem,
  GridSortModel,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { useQuery } from "@apollo/client";
import {
  Box,
  Chip,
  FormControlLabel,
  FormGroup,
  Stack,
  Switch,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { add, parseISO } from "date-fns";
import { useMsal } from "@azure/msal-react";
import {
  DealRiskLevels_Enum,
  DealStatus_Enum,
  GetDealsDocument,
  GetDealsQuery,
  LafActionChoices_Enum,
} from "../../generated/graphql-operations";
import {
  checkAndFormatDate,
  convertDaysToYearsMonthsDays,
} from "../../utils/DateUtils";
import {
  ACTIVE_DEAL_STATUS_EXCLUSIONS,
  AU_DATE_FORMAT,
  DEAL_APPROVAL_PHASE_NAME,
  DealPhaseStatus,
  DealType,
  HANDOVER_PHASE_NAME,
  INACTIVE_DEAL_STATUSES,
  LAF_PHASE_NAME,
  LEASE_ADMIN_NAME,
  LEASE_EXECUTED_AND_UPLOADED,
  LEASE_PHASE_NAME,
  LETTER_OF_ACCEPTANCE_SENT,
  SURVEY_PLAN_ATTACHED,
  TENANCY_DELIVERY_PHASE_NAME,
  VIRTUAL_UNIT_LABEL_SUFFIX,
} from "../../utils/CommonVariables";
import {
  formatDealStatusText,
  riskSxColor,
  statusSxColor,
} from "../../utils/CommonFunctions";
import PersistedDataGrid from "../PersistedDataGrid";

export interface DealsListProps {
  toolBar: boolean;
  filterToUser?: boolean;
  statusGroup?: "active" | "completed";
}

const CustomToolbar = () => {
  return (
    <Stack direction="row" justifyContent="space-between" sx={{ p: 2 }}>
      <Stack direction="row" spacing={2}>
        <GridToolbarFilterButton />
        <GridToolbarColumnsButton />
      </Stack>
      <GridToolbarQuickFilter variant="filled" />
    </Stack>
  );
};

const computeLeaseExecutedAndUploaded = (deal: GetDealsQuery["Deals"][0]) => {
  return !!deal?.DealPhases?.find(
    (phase) =>
      phase.name?.toLowerCase() === LEASE_PHASE_NAME.toLowerCase() &&
      phase.DealActionItem.find(
        (dealActionItem) =>
          dealActionItem.content
            ?.toLowerCase()
            .includes(LEASE_EXECUTED_AND_UPLOADED.toLowerCase()) &&
          dealActionItem.state
      )
  );
};

type LeaseExecutedAndUploaded = {
  leaseExecutedAndUploaded: boolean;
};

const getLarActionsDeals = (
  data: (GetDealsQuery["Deals"][0] & LeaseExecutedAndUploaded)[] | undefined
) => {
  const larActionFilteredDeals = data?.filter((deal) => {
    // Include deals that are also completed
    const dealCompleted = deal.status === DealStatus_Enum.Completed;
    // Deal status is approved (all deals)
    const dealApproved = deal.status === DealStatus_Enum.Approved;
    if (!dealApproved && !dealCompleted) return false;

    // If a Yardi Active leases does not exist, it
    // means the Tenant is no longer active, thus should not show up in LAR toggle
    // As it means this tenant is terminated in Yardi
    // Note: This does not apply to new deals, as new deals don't have any active leases,
    // hence the check for deal_made_from_active_lease
    const yardiTenantDeal = deal?.YardiTenants_Deals?.[0];
    const activeTenant = yardiTenantDeal?.YardiActiveLeases;
    if (yardiTenantDeal && !activeTenant && deal.deal_made_from_active_lease) {
      return false;
    }

    // What we are doing here is determining if any Yardi Active leases
    // End dates(plus 1 month to catch outliers)
    // are greater than or equal to any lease easy deals expiry date
    // If so they are old deals or deals that have just been activated in Yardi
    // Meaning LAR team don't care about them anymore
    const MatchingActiveTenantEndDate = activeTenant?.EndDate;
    const handoverDate = deal?.DealData?.[0]?.handover_date;
    if (MatchingActiveTenantEndDate && handoverDate) {
      const daysToEndDate = deal?.DealData?.[0]?.lease_term ?? 0;
      const ActiveTenantParse = parseISO(MatchingActiveTenantEndDate);
      const handoverDateParse = parseISO(handoverDate);
      const expireDate = add(handoverDateParse, { days: daysToEndDate });

      // Extra month to catch any outliers(The deal is a bit late to activate)
      if (expireDate < add(ActiveTenantParse, { months: 1 })) {
        return false;
      }
    }

    // Letter of Acceptance checklist item ticked in the ‘Deal Approval’ phase (All deals)
    // If letter of acceptance is not ticked, return false and filter out the deal
    const letterOfAcceptanceTicked = deal.DealPhases.find(
      (phase) =>
        phase.name?.toLowerCase() === DEAL_APPROVAL_PHASE_NAME.toLowerCase() &&
        phase.DealActionItem.find(
          (dealActionItem) =>
            dealActionItem.content
              ?.toLowerCase()
              .includes(LETTER_OF_ACCEPTANCE_SENT.toLowerCase()) &&
            dealActionItem.state
        )
    );
    if (!letterOfAcceptanceTicked) return false;

    const checkDealTypes = [
      DealType.New,
      DealType.Opportunity,
      DealType.NewLeaseChangeOwnership,
      DealType.RemixRelocation,
      DealType.Churn,
      DealType.CurrentYearVacancy,
    ];

    if (checkDealTypes.includes(deal.DealType.title!)) {
      // Lease Executed and Uploaded ticked in the 'Lease' Phase (New deals only)
      // If DealType is New and Lease Executed and Uploaded is not ticked, return false and filter out the deal
      if (!deal.leaseExecutedAndUploaded) return false;

      // Handover phase completed (New deals only)
      // If DealType is New and HandoverPhase is not completed, return false and filter out the deal
      const handoverCompleted = deal.DealPhases.find(
        (phase) =>
          phase.name?.toLowerCase() === HANDOVER_PHASE_NAME.toLowerCase() &&
          phase.status_id === DealPhaseStatus.Complete
      );
      if (!handoverCompleted) return false;
    }

    // Survey Plan attached (Virtual units only)
    // If Virtual Unit deal and Survey Plan is not attached, return false and filter out the deal
    if (deal.DealData[0].DealsUnits && !deal.DealData[0].DealsUnits[0].Unit) {
      const surveyPlanAttached = deal.DealPhases.find(
        (phase) =>
          // Appears in Lease phase in preprod but Registration phase in local
          phase.name?.toLowerCase() === LEASE_PHASE_NAME.toLowerCase() &&
          phase.DealActionItem.find(
            (dealActionItem) =>
              dealActionItem.content
                ?.toLowerCase()
                .includes(SURVEY_PLAN_ATTACHED.toLowerCase()) &&
              dealActionItem.state
          )
      );
      if (!surveyPlanAttached) return false;
    }

    return true;
  });
  return larActionFilteredDeals;
};

export const DealsList = ({
  toolBar,
  filterToUser = false,
  statusGroup = "active",
}: DealsListProps) => {
  const { instance } = useMsal();
  const userId = instance.getActiveAccount()?.idTokenClaims?.oid;

  const {
    data: queryData,
    loading,
    error,
  } = useQuery(
    GetDealsDocument,
    filterToUser
      ? {
          variables: {
            filters: {
              _and: [
                {
                  status: {
                    _nin: [
                      DealStatus_Enum.Superseded,
                      DealStatus_Enum.Deleted,
                      DealStatus_Enum.Cancelled,
                      DealStatus_Enum.Completed,
                      DealStatus_Enum.Archived,
                    ],
                  },
                },
                {
                  _or: [
                    { owner: { _eq: userId } },
                    { la_assignee: { _eq: userId } },
                    { td_assignee: { _eq: userId } },
                  ],
                },
                {
                  _not: {
                    _and: [
                      { td_assignee: { _eq: userId } },
                      {
                        DealPhases: {
                          name: { _eq: TENANCY_DELIVERY_PHASE_NAME },
                          status_id: { _eq: DealPhaseStatus.Complete },
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        }
      : {
          variables: {
            filters: {
              status:
                statusGroup === "completed"
                  ? { _in: INACTIVE_DEAL_STATUSES }
                  : { _nin: ACTIVE_DEAL_STATUS_EXCLUSIONS },
            },
          },
        }
  );

  // Work out executed and Uploaded as need to use it in LAR & as a column
  const data = useMemo(
    () =>
      queryData?.Deals.map((deal) => {
        const leaseExecutedAndUploaded = computeLeaseExecutedAndUploaded(deal);
        return {
          ...deal,
          leaseExecutedAndUploaded,
        };
      }),
    [queryData]
  );

  const navigate = useNavigate();
  const [larAction, setLarAction] = useState(false);

  const larActionFilteredDeals = useMemo(
    () => getLarActionsDeals(data),
    [data]
  );

  const handleLarActionchange = (
    event: React.SyntheticEvent,
    isChecked: boolean
  ) => {
    setLarAction(isChecked);
  };

  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);

  const riskLevelColumn: GridColDef<
    NonNullable<GetDealsQuery["Deals"][0] & LeaseExecutedAndUploaded>
  > = {
    field: "riskLevel",
    headerName: "Risk",
    renderCell: (params) =>
      params.value && (
        <Chip label={params.value} sx={riskSxColor(params.value)} />
      ),
    sortComparator: (
      v1: DealRiskLevels_Enum,
      v2: DealRiskLevels_Enum,
      cellParams
    ) => {
      const riskLevelOrder = [
        DealRiskLevels_Enum.Low,
        DealRiskLevels_Enum.Medium,
        DealRiskLevels_Enum.High,
      ];

      const sortColumn = sortModel.find(
        (sm: GridSortItem) => sm.field === cellParams.field
      );

      if (sortColumn && sortColumn.sort === "asc") {
        if (!v1) return 1;
        if (!v2) return -1;
      }

      return riskLevelOrder.indexOf(v1) - riskLevelOrder.indexOf(v2);
    },
    flex: 1,
    valueGetter: (params) => params.row.risk_level,
  };

  const columns: GridColDef<
    NonNullable<GetDealsQuery["Deals"][0] & LeaseExecutedAndUploaded>
  >[] = [
    {
      field: "centre",
      headerName: "Centre",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealData[0]?.DealsUnits[0]?.Unit?.Property?.label ??
        params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.Property?.label ??
        "",
    },
    {
      field: "shop",
      headerName: "Shop",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealData[0]?.DealsUnits[0]?.Unit?.label ??
        (params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.label
          ? `${params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.label}${VIRTUAL_UNIT_LABEL_SUFFIX}`
          : null) ??
        "",
    },
    {
      field: "property_code",
      headerName: "Property code",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealData[0]?.DealsUnits[0]?.Unit?.Property?.property_code ??
        params.row.DealData[0]?.DealsUnits[0]?.VirtualUnit?.Property
          ?.property_code ??
        "",
    },
    {
      field: "tradingname",
      headerName: "Trading name",
      flex: 1,
      valueGetter: (params) => params.row.trading_name,
    },
    {
      field: "tenant_code",
      headerName: "Tenant ID",
      flex: 1,
      valueGetter: (params) =>
        params.row.tenant_code ??
        params.row.YardiTenants_Deals?.[0]?.TenantCode ??
        "",
    },
    {
      field: "phase",
      headerName: "Phase",
      flex: 1,
      valueGetter: (params) =>
        params.row.DealPhases.filter((item) => !item.is_tenancy_delivery).find(
          (item) => item.status_id === DealPhaseStatus.Active
        )?.name ?? "",
    },
    {
      field: "leasestart",
      headerName: "Lease start date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) =>
        params.row.DealData[0]?.lease_start
          ? parseISO(params.row.DealData[0]?.lease_start)
          : undefined,
    },
    {
      field: "handover_date",
      headerName: "Handover date",
      flex: 1,
      type: "date",
      valueFormatter: (params) =>
        checkAndFormatDate(params.value, AU_DATE_FORMAT),
      valueGetter: (params) =>
        params.row.DealData[0]?.handover_date
          ? parseISO(params.row.DealData[0]?.handover_date)
          : undefined,
    },
    {
      field: "lease_term",
      headerName: "Lease term",
      flex: 1,
      valueGetter: (params) => params.row.DealData[0]?.lease_term,
      renderCell: (params) =>
        convertDaysToYearsMonthsDays(
          parseISO(params.row.DealData[0]?.lease_start),
          params.row.DealData[0]?.lease_term
        ),
    },
    {
      field: "dealowner",
      headerName: "Deal owner",
      flex: 1,
      valueGetter: (params) => params.row.Owner?.user_full_name ?? "",
    },
    {
      field: "laAssignee",
      headerName: "LA assignee",
      flex: 1,
      valueGetter: (params) => params.row.LA_Assignee?.user_full_name ?? "",
    },
    {
      field: "tdAssignee",
      headerName: "TD assignee",
      flex: 1,
      valueGetter: (params) => params.row.TD_Assignee?.user_full_name ?? "",
    },
    {
      field: "laf_version",
      headerName: "Version",
      flex: 1,
      valueGetter: (params) => params.row.laf_version,
    },
    {
      field: "text",
      headerName: "Comments",
      flex: 1,
      valueGetter: (params) => params.row.Comments?.[0]?.text ?? "",
    },
    {
      field: "lar-comments",
      headerName: "LAR Comments",
      flex: 1,
      valueGetter: (params) =>
        params.row.Comments?.find((comment) =>
          comment.CommentTags.find(
            (tag) => tag.Tag?.name?.toLowerCase() === "lar"
          )
        )?.text ?? "",
    },
    {
      field: "lafAprover",
      headerName: "LAF reviewee",
      flex: 1,
      valueGetter: (params) => {
        const dealApproval = params.row.DealData?.[0]?.DealLAFApprovals;
        if (
          dealApproval?.laf_action &&
          [
            LafActionChoices_Enum.Approved,
            LafActionChoices_Enum.Escalation,
            LafActionChoices_Enum.LeaseAdminApproval,
          ].includes(dealApproval?.laf_action)
        ) {
          return dealApproval?.ActiveApprover?.user_full_name;
        }
        const LafPhaseActive =
          params.row.DealPhases.find((item) => item.name === LAF_PHASE_NAME)
            ?.status_id === DealPhaseStatus.Active;

        if (
          dealApproval?.laf_action === LafActionChoices_Enum.RequestChange ||
          (LafPhaseActive && dealApproval === undefined)
        ) {
          return LEASE_ADMIN_NAME;
        }
        return undefined;
      },
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => (
        <Chip
          label={formatDealStatusText(params.value)}
          sx={statusSxColor(params.value)}
        />
      ),
    },
    {
      field: "leaseExecutedAndUploaded",
      headerName: "Lease Executed",
      flex: 1,
      valueGetter: (params) =>
        params.row.leaseExecutedAndUploaded ? "Yes" : null,
    },
    {
      field: "type",
      headerName: "Type",
      flex: 1,
      valueGetter: (params) => params.row.DealType.title,
    },
    riskLevelColumn,
  ];

  return (
    <Box>
      <Box>
        {statusGroup !== "completed" && (
          <FormGroup>
            <FormControlLabel
              control={<Switch />}
              label="LAR Action"
              onChange={handleLarActionchange}
            />
          </FormGroup>
        )}
        <PersistedDataGrid
          classes={{
            sortIcon: "color:red;margin-top:75px;",
          }}
          storageKey={
            statusGroup === "completed" ? "completedDeals" : undefined
          }
          rows={(larAction ? larActionFilteredDeals : data) ?? []}
          columns={columns}
          loading={loading || error !== undefined}
          onCellClick={(params, event) => {
            if (!event.ctrlKey) {
              navigate(`/deals/${params.row.id}`);
            }
          }}
          components={toolBar ? { Toolbar: CustomToolbar } : {}}
          applyFilters={!filterToUser}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
        />
      </Box>
    </Box>
  );
};

export default DealsList;
